import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Building2, 
  Award,
  Calendar,
  DollarSign,
  Users,
  Briefcase,
  Tags,
  Save,
  Loader2,
  Plus,
  X,
  Settings,
  MapPin,
  Trophy,
  Handshake,
  FileCheck,
  BarChart3,
  FolderKanban,
  Upload,
  FileText,
  Image,
  Trash2,
  Download,
  Palette,
  Package
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import ProductHierarchyManager from "../components/ProductHierarchyManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanySettings = () => {
  const { isDeptHead } = useAuth();
  const canEdit = isDeptHead();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState("general");
  
  // Assets state
  const [assets, setAssets] = useState({ logo: null, letterhead: null });
  const [uploadingAsset, setUploadingAsset] = useState(null);
  
  const [profile, setProfile] = useState({
    company_name: "AXISINDIA",
    registration_no: "",
    gstin: "",
    certifications: [],
    years_experience: 0,
    annual_turnover: 0,
    employee_count: 0,
    technical_capabilities: [],
    key_industries: [],
    sectors: [],
    service_offerings: [],
    key_clients: [],
    geographic_presence: [],
    total_projects_completed: 0,
    govt_projects_count: 0,
    private_projects_count: 0,
    ongoing_projects_count: 0,
    largest_project_value: 0,
    min_project_value: 0,
    max_project_value: 0,
    net_worth_inr: 0,
    turnover_3yr_inr: [],
    oem_authorizations: [],
    past_projects: [],
    office_locations: [],
    operational_states: [],
    empanelments: [],
    msme_uin: "",
    mii_status: "",
    awards: [],
    partnerships: [],
    average_project_value: 0,
    profit_margin: 0
  });

  const [notifications, setNotifications] = useState({
    email_enabled: false,
    email_address: "",
    days_before_deadline: 7,
    notify_on_upload: true,
    notify_on_analysis_complete: true
  });

  const [newItems, setNewItems] = useState({
    certification: "",
    capability: "",
    industry: "",
    sector: "",
    service: "",
    client: "",
    location: "",
    geo_presence: "",
    state: "",
    empanelment: "",
    award: "",
    partnership: "",
  });
  const [newTurnover, setNewTurnover] = useState({ fy: "", value_inr: "" });
  const [newOem, setNewOem] = useState({ oem: "", category: "", valid_until: "" });
  const [newProject, setNewProject] = useState({ name: "", client: "", value_inr: "", year: "" });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/v2/settings/fine-tuning`);
      const cp = res.data?.company_profile || {};
      setProfile(prev => ({ ...prev, ...cp }));
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Template upload handler
  const handleTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingTemplate(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_name', templateName || file.name);
    formData.append('template_type', templateType);
    
    try {
      const res = await axios.post(`${API}/templates/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTemplates([...templates, res.data.template]);
      setTemplateName("");
      toast.success("Template uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload template");
    } finally {
      setUploadingTemplate(false);
      e.target.value = '';
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success("Template deleted");
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleDownloadTemplate = async (template) => {
    try {
      const response = await axios.get(`${API}/templates/${template.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', template.original_filename || `template.${template.file_type}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  // Asset upload handler (logo/letterhead)
  const handleAssetUpload = async (e, assetType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingAsset(assetType);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/assets/${assetType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAssets(prev => ({ ...prev, [assetType]: res.data.asset }));
      toast.success(`${assetType.charAt(0).toUpperCase() + assetType.slice(1)} uploaded successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to upload ${assetType}`);
    } finally {
      setUploadingAsset(null);
      e.target.value = '';
    }
  };

  const handleDeleteAsset = async (assetType) => {
    try {
      await axios.delete(`${API}/assets/${assetType}`);
      setAssets(prev => ({ ...prev, [assetType]: null }));
      toast.success(`${assetType.charAt(0).toUpperCase() + assetType.slice(1)} deleted`);
    } catch (error) {
      toast.error(`Failed to delete ${assetType}`);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const current = await axios.get(`${API}/v2/settings/fine-tuning`).then(r => r.data).catch(() => ({}));
      const { _scope_source, ...rest } = current;
      await axios.put(`${API}/v2/settings/fine-tuning`, { ...rest, company_profile: profile });
      toast.success("Company profile saved successfully!");
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-settings`, notifications);
      toast.success("Notification settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addToList = (field, itemKey) => {
    if (newItems[itemKey]?.trim()) {
      setProfile({
        ...profile,
        [field]: [...(profile[field] || []), newItems[itemKey].trim()]
      });
      setNewItems({ ...newItems, [itemKey]: "" });
    }
  };

  const removeFromList = (field, index) => {
    setProfile({
      ...profile,
      [field]: profile[field].filter((_, i) => i !== index)
    });
  };

  // Input handler that preserves focus
  const handleNewItemChange = (itemKey, value) => {
    setNewItems(prev => ({ ...prev, [itemKey]: value }));
  };

  // Render a list input field - using a function that returns JSX instead of a component
  const renderListInput = (field, itemKey, placeholder, badgeColor = "bg-gray-100 text-gray-700") => (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          placeholder={placeholder}
          value={newItems[itemKey] || ""}
          onChange={(e) => handleNewItemChange(itemKey, e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addToList(field, itemKey)}
        />
        <Button onClick={() => addToList(field, itemKey)} size="icon">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        {(profile[field] || []).map((item, idx) => (
          <Badge key={idx} className={`${badgeColor} px-3 py-1`}>
            {item}
            <button onClick={() => removeFromList(field, idx)} className="ml-2 hover:text-red-600">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        {(!profile[field] || profile[field].length === 0) && (
          <span className="text-sm text-[#64748B]">None added</span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen" data-testid="settings-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-[#0F172A] font-['Manrope'] tracking-tight">
          Company Settings
        </h1>
        <p className="text-[#64748B] mt-1">
          Configure your company profile for accurate bid scoring and proposal generation
        </p>
      </div>

      {!canEdit && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
          </svg>
          View only — contact an admin or manager to make changes.
        </div>
      )}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F1F5F9] p-1 flex-wrap h-auto">
          <TabsTrigger value="basic" data-testid="tab-basic">
            <Building2 className="w-4 h-4 mr-2" />
            Basic Info
          </TabsTrigger>
          <TabsTrigger value="capabilities" data-testid="tab-capabilities">
            <Briefcase className="w-4 h-4 mr-2" />
            Capabilities
          </TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials">
            <Award className="w-4 h-4 mr-2" />
            Credentials
          </TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">
            <Palette className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
        </TabsList>

        <fieldset disabled={!canEdit} className={!canEdit ? "opacity-80" : ""}>
        {/* Basic Info Tab */}
        <TabsContent value="basic" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#007AFF]" />
                  Company Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={profile.company_name}
                    onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Years of Experience
                    </Label>
                    <Input
                      type="number"
                      value={profile.years_experience}
                      onChange={(e) => setProfile({ ...profile, years_experience: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Employee Count
                    </Label>
                    <Input
                      type="number"
                      value={profile.employee_count}
                      onChange={(e) => setProfile({ ...profile, employee_count: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Registration No.</Label>
                    <Input
                      value={profile.registration_no || ""}
                      onChange={(e) => setProfile({ ...profile, registration_no: e.target.value })}
                      placeholder="e.g., U72200MH2005PTC123456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>GSTIN</Label>
                    <Input
                      value={profile.gstin || ""}
                      onChange={(e) => setProfile({ ...profile, gstin: e.target.value })}
                      placeholder="e.g., 27AAAAA0000A1Z5"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Financial Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Project Value (₹)</Label>
                    <Input
                      type="number"
                      value={profile.min_project_value || 0}
                      onChange={(e) => setProfile({ ...profile, min_project_value: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 1000000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Project Value (₹)</Label>
                    <Input
                      type="number"
                      value={profile.max_project_value || 0}
                      onChange={(e) => setProfile({ ...profile, max_project_value: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 50000000"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Net Worth (₹)</Label>
                    <Input
                      type="number"
                      value={profile.net_worth_inr || 0}
                      onChange={(e) => setProfile({ ...profile, net_worth_inr: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Largest Project (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={profile.largest_project_value}
                      onChange={(e) => setProfile({ ...profile, largest_project_value: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Last 3 Years Turnover</Label>
                  <div className="space-y-2">
                    {(profile.turnover_3yr_inr || []).map((t, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-slate-50 px-3 py-1.5 rounded-md">
                        <span className="font-mono text-slate-600 w-20">{t.fy}</span>
                        <span className="flex-1 text-slate-800">₹{Number(t.value_inr).toLocaleString("en-IN")}</span>
                        <button onClick={() => setProfile({ ...profile, turnover_3yr_inr: profile.turnover_3yr_inr.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-rose-500"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    {(profile.turnover_3yr_inr || []).length < 3 && (
                      <div className="flex gap-2">
                        <Input placeholder="FY e.g. 2023-24" value={newTurnover.fy} onChange={(e) => setNewTurnover(p => ({ ...p, fy: e.target.value }))} className="w-32" />
                        <Input placeholder="Value (₹)" type="number" value={newTurnover.value_inr} onChange={(e) => setNewTurnover(p => ({ ...p, value_inr: e.target.value }))} />
                        <Button size="icon" onClick={() => {
                          if (!newTurnover.fy || !newTurnover.value_inr) return;
                          setProfile({ ...profile, turnover_3yr_inr: [...(profile.turnover_3yr_inr || []), { fy: newTurnover.fy, value_inr: parseFloat(newTurnover.value_inr) }] });
                          setNewTurnover({ fy: "", value_inr: "" });
                        }}><Plus className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0] lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-500" />
                  Geographic Presence
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label className="mb-3 block">Countries / Regions <span className="text-xs text-slate-400">(used by Bid Matrix)</span></Label>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g., India, Middle East"
                        value={newItems.geo_presence || ""}
                        onChange={(e) => handleNewItemChange("geo_presence", e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addToList("geographic_presence", "geo_presence")}
                      />
                      <Button onClick={() => addToList("geographic_presence", "geo_presence")} size="icon">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {(profile.geographic_presence || []).map((item, idx) => (
                        <Badge key={idx} className="bg-violet-100 text-violet-700 px-3 py-1">
                          {item}
                          <button onClick={() => removeFromList("geographic_presence", idx)} className="ml-2 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {(!profile.geographic_presence || profile.geographic_presence.length === 0) && (
                        <span className="text-sm text-[#64748B]">None added</span>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="mb-3 block">Office Locations</Label>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g., Delhi, Mumbai"
                        value={newItems.location || ""}
                        onChange={(e) => handleNewItemChange("location", e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addToList("office_locations", "location")}
                      />
                      <Button onClick={() => addToList("office_locations", "location")} size="icon">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {(profile.office_locations || []).map((item, idx) => (
                        <Badge key={idx} className="bg-red-100 text-red-700 px-3 py-1">
                          {item}
                          <button onClick={() => removeFromList("office_locations", idx)} className="ml-2 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {(!profile.office_locations || profile.office_locations.length === 0) && (
                        <span className="text-sm text-[#64748B]">None added</span>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="mb-3 block">Operational States</Label>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g., Maharashtra, Karnataka"
                        value={newItems.state || ""}
                        onChange={(e) => handleNewItemChange("state", e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addToList("operational_states", "state")}
                      />
                      <Button onClick={() => addToList("operational_states", "state")} size="icon">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {(profile.operational_states || []).map((item, idx) => (
                        <Badge key={idx} className="bg-blue-100 text-blue-700 px-3 py-1">
                          {item}
                          <button onClick={() => removeFromList("operational_states", idx)} className="ml-2 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {(!profile.operational_states || profile.operational_states.length === 0) && (
                        <span className="text-sm text-[#64748B]">None added</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Capabilities Tab */}
        <TabsContent value="capabilities" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-indigo-600" />
                  Technical Capabilities
                </CardTitle>
                <CardDescription>Technologies and skills your team possesses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Cloud Computing, AI/ML, Java"
                      value={newItems.capability || ""}
                      onChange={(e) => handleNewItemChange("capability", e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addToList("technical_capabilities", "capability")}
                    />
                    <Button onClick={() => addToList("technical_capabilities", "capability")} size="icon">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[32px]">
                    {(profile.technical_capabilities || []).map((item, idx) => (
                      <Badge key={idx} className="bg-indigo-100 text-indigo-700 px-3 py-1">
                        {item}
                        <button onClick={() => removeFromList("technical_capabilities", idx)} className="ml-2 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!profile.technical_capabilities || profile.technical_capabilities.length === 0) && (
                      <span className="text-sm text-[#64748B]">None added</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Tags className="w-5 h-5 text-amber-600" />
                  Service Offerings
                </CardTitle>
                <CardDescription>Services your company provides</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Software Development, Consulting"
                      value={newItems.service || ""}
                      onChange={(e) => handleNewItemChange("service", e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addToList("service_offerings", "service")}
                    />
                    <Button onClick={() => addToList("service_offerings", "service")} size="icon">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[32px]">
                    {(profile.service_offerings || []).map((item, idx) => (
                      <Badge key={idx} className="bg-amber-100 text-amber-700 px-3 py-1">
                        {item}
                        <button onClick={() => removeFromList("service_offerings", idx)} className="ml-2 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!profile.service_offerings || profile.service_offerings.length === 0) && (
                      <span className="text-sm text-[#64748B]">None added</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  Industry Sectors
                </CardTitle>
                <CardDescription>Sectors where your company has experience</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Government, Healthcare, BFSI, IT"
                      value={newItems.sector || ""}
                      onChange={(e) => handleNewItemChange("sector", e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addToList("sectors", "sector")}
                    />
                    <Button onClick={() => addToList("sectors", "sector")} size="icon">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[32px]">
                    {(profile.sectors || []).map((item, idx) => (
                      <Badge key={idx} className="bg-emerald-100 text-emerald-700 px-3 py-1">
                        {item}
                        <button onClick={() => removeFromList("sectors", idx)} className="ml-2 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!profile.sectors || profile.sectors.length === 0) && (
                      <span className="text-sm text-[#64748B]">None added</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Key Industries
                </CardTitle>
                <CardDescription>Primary industries for bid matching (used by Bid Matrix)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Oil & Gas, Water Treatment, Power"
                      value={newItems.industry || ""}
                      onChange={(e) => handleNewItemChange("industry", e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addToList("key_industries", "industry")}
                    />
                    <Button onClick={() => addToList("key_industries", "industry")} size="icon">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[32px]">
                    {(profile.key_industries || []).map((item, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-700 px-3 py-1">
                        {item}
                        <button onClick={() => removeFromList("key_industries", idx)} className="ml-2 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!profile.key_industries || profile.key_industries.length === 0) && (
                      <span className="text-sm text-[#64748B]">None added</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Credentials Tab */}
        <TabsContent value="credentials" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-600" />
                  Certifications
                </CardTitle>
                <CardDescription>ISO, CMMI, and other certifications</CardDescription>
              </CardHeader>
              <CardContent>
                {renderListInput("certifications", "certification", "e.g., ISO 9001:2015, CMMI Level 5", "bg-emerald-100 text-emerald-700")}
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-blue-600" />
                  Empanelments
                </CardTitle>
                <CardDescription>Government and institutional empanelments</CardDescription>
              </CardHeader>
              <CardContent>
                {renderListInput("empanelments", "empanelment", "e.g., GeM, NICSI, STQC", "bg-blue-100 text-blue-700")}
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Handshake className="w-5 h-5 text-indigo-600" />
                  Partnerships
                </CardTitle>
                <CardDescription>Technology and business partnerships</CardDescription>
              </CardHeader>
              <CardContent>
                {renderListInput("partnerships", "partnership", "e.g., Microsoft Gold Partner, AWS Partner", "bg-indigo-100 text-indigo-700")}
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-600" />
                  Awards & Recognition
                </CardTitle>
                <CardDescription>Industry awards and recognitions</CardDescription>
              </CardHeader>
              <CardContent>
                {renderListInput("awards", "award", "e.g., Best IT Company 2024", "bg-amber-100 text-amber-700")}
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-slate-600" />
                  Compliance Flags
                </CardTitle>
                <CardDescription>MSME / MII status for GeM and govt tenders</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>MSME UIN</Label>
                  <Input
                    value={profile.msme_uin || ""}
                    onChange={(e) => setProfile({ ...profile, msme_uin: e.target.value })}
                    placeholder="Leave blank if not MSME registered"
                  />
                </div>
                <div className="space-y-2">
                  <Label>MII Status</Label>
                  <select
                    value={profile.mii_status || ""}
                    onChange={(e) => setProfile({ ...profile, mii_status: e.target.value })}
                    className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md bg-white"
                  >
                    <option value="">Not applicable</option>
                    <option value="Class I">Class I (≥50% local content)</option>
                    <option value="Class II">Class II (20–50% local content)</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0] lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Package className="w-5 h-5 text-indigo-600" />
                  OEM Authorisations
                </CardTitle>
                <CardDescription>Manufacturer authorisation letters (MAFs) — used by Bid Matrix</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(profile.oem_authorizations || []).map((o, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-slate-50 px-3 py-2 rounded-md text-sm">
                    <span className="font-medium text-slate-800 w-28 truncate">{o.oem}</span>
                    <span className="text-slate-500 flex-1">{o.category}</span>
                    <span className="text-slate-400 text-xs">{o.valid_until || "—"}</span>
                    <button onClick={() => setProfile({ ...profile, oem_authorizations: profile.oem_authorizations.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-rose-500"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input placeholder="OEM name" value={newOem.oem} onChange={(e) => setNewOem(p => ({ ...p, oem: e.target.value }))} className="w-36" />
                  <Input placeholder="Category" value={newOem.category} onChange={(e) => setNewOem(p => ({ ...p, category: e.target.value }))} />
                  <Input placeholder="Valid until" type="date" value={newOem.valid_until} onChange={(e) => setNewOem(p => ({ ...p, valid_until: e.target.value }))} className="w-40" />
                  <Button size="icon" onClick={() => {
                    if (!newOem.oem) return;
                    setProfile({ ...profile, oem_authorizations: [...(profile.oem_authorizations || []), { ...newOem }] });
                    setNewOem({ oem: "", category: "", valid_until: "" });
                  }}><Plus className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0] lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <FolderKanban className="w-5 h-5 text-teal-600" />
                  Past Projects
                </CardTitle>
                <CardDescription>Reference projects shown to Bid Matrix when evaluating experience criteria</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(profile.past_projects || []).map((p, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-slate-50 px-3 py-2 rounded-md text-sm">
                    <span className="font-medium text-slate-800 flex-1 truncate">{p.name}</span>
                    <span className="text-slate-500 w-28 truncate">{p.client}</span>
                    <span className="text-slate-400 w-20 text-right">₹{p.value_inr ? Number(p.value_inr).toLocaleString("en-IN") : "—"}</span>
                    <span className="text-slate-400 w-12">{p.year}</span>
                    <button onClick={() => setProfile({ ...profile, past_projects: profile.past_projects.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-rose-500"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input placeholder="Project name" value={newProject.name} onChange={(e) => setNewProject(p => ({ ...p, name: e.target.value }))} />
                  <Input placeholder="Client" value={newProject.client} onChange={(e) => setNewProject(p => ({ ...p, client: e.target.value }))} className="w-36" />
                  <Input placeholder="Value (₹)" type="number" value={newProject.value_inr} onChange={(e) => setNewProject(p => ({ ...p, value_inr: e.target.value }))} className="w-32" />
                  <Input placeholder="Year" value={newProject.year} onChange={(e) => setNewProject(p => ({ ...p, year: e.target.value }))} className="w-20" />
                  <Button size="icon" onClick={() => {
                    if (!newProject.name) return;
                    setProfile({ ...profile, past_projects: [...(profile.past_projects || []), { name: newProject.name, client: newProject.client, value_inr: parseFloat(newProject.value_inr) || 0, year: newProject.year, scope: [] }] });
                    setNewProject({ name: "", client: "", value_inr: "", year: "" });
                  }}><Plus className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <Image className="w-5 h-5 text-[#007AFF]" />
                  Company Logo
                </CardTitle>
                <CardDescription>Upload your company logo for proposals and reports</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {assets.logo ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                      <img 
                        src={`${API}/assets/logo/file`} 
                        alt="Company Logo" 
                        className="max-h-32 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#64748B]">{assets.logo.original_filename}</span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteAsset('logo')}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-8 text-center">
                    <Image className="w-12 h-12 mx-auto text-[#E2E8F0] mb-3" />
                    <p className="text-sm text-[#64748B] mb-4">PNG, JPG, SVG up to 5MB</p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg,.svg,.webp"
                        className="hidden"
                        onChange={(e) => handleAssetUpload(e, 'logo')}
                        disabled={uploadingAsset === 'logo'}
                      />
                      <Button asChild disabled={uploadingAsset === 'logo'}>
                        <span>
                          {uploadingAsset === 'logo' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Logo
                        </span>
                      </Button>
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Letterhead Upload */}
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Letterhead
                </CardTitle>
                <CardDescription>Upload letterhead template for formal documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {assets.letterhead ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-[#F1F5F9] rounded-lg flex items-center gap-3">
                      <FileText className="w-10 h-10 text-emerald-600" />
                      <div className="flex-1">
                        <p className="font-medium text-[#0F172A]">{assets.letterhead.original_filename}</p>
                        <p className="text-sm text-[#64748B]">
                          {(assets.letterhead.file_size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteAsset('letterhead')}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove Letterhead
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-8 text-center">
                    <FileText className="w-12 h-12 mx-auto text-[#E2E8F0] mb-3" />
                    <p className="text-sm text-[#64748B] mb-4">PDF, DOCX, or images</p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.docx,.png,.jpg,.jpeg"
                        className="hidden"
                        onChange={(e) => handleAssetUpload(e, 'letterhead')}
                        disabled={uploadingAsset === 'letterhead'}
                      />
                      <Button asChild disabled={uploadingAsset === 'letterhead'}>
                        <span>
                          {uploadingAsset === 'letterhead' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Letterhead
                        </span>
                      </Button>
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        </fieldset>
      </Tabs>

      {/* Save Button — admin/manager only */}
      {canEdit && (
        <div className="fixed bottom-8 right-8 z-50">
          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            size="lg"
            className="bg-[#0F172A] hover:bg-[#1E293B] shadow-lg"
            data-testid="save-profile-btn"
          >
            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
            Save Company Profile
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanySettings;
